import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import PnoeDetailSection from "./PnoeDetailSection";
import FilterSelect from "../prescriptionTab/FilterSelect";
import ActionButton from "../../../../common/ui/ActionButton";
import EditSvg from "../../../../common/svgs/EditSvg";
import CustomTooltip from "../common/CustomTooltip";
import {
  fetchAllBreathPages,
  getPnoeVo2Data,
} from "../../../../common/network/Api";
import {
  PnoeMeasurementCatalogs,
  PnoePhase,
  PnoeVo2Field,
  PnoeVo2Record,
  toNumber,
} from "../../entities/pnoeMeasurement";
import { getPhaseTypeLabel } from "../../utils/pnoeMeasurementStatus";
import { formatPhaseLoad, hasPhaseLoad } from "./pnoeFieldFormatting";
import {
  PnoeSeriesDefinition,
  SMOOTHING_OPTIONS,
  buildChartPoints,
  buildSeriesDefinitions,
  formatSeriesValue,
  decimatePoints,
  formatElapsed,
  smoothChartPoints,
} from "./pnoeChartSeries";

/**
 * What the chart opens on, when the test recorded them — the measures the
 * page led with before the column list became the backend's. A test without
 * them (no gas exchange, say) falls back to its first two measures.
 */
const PREFERRED_SERIES = ["vo2_ml_min", "hr"];

/**
 * At most four lines at a time (Figma node 16883:66653), because the design
 * gives the chart exactly four series colours. Picking a fifth measure does
 * nothing — one has to be deselected first.
 */
const MAX_SERIES = 4;

/**
 * The statistics the chart can overlay (Figma node 16883:66653, "Statistics 2"
 * — both on by default, which is what its badge shows).
 *
 * `mean` draws a dashed segment across each phase; `max` marks each phase
 * type's maximum with a larger dot on the line and names it in the tooltip.
 * Both are computed per phase *type*, so a ramp test's steps share one
 * average and one maximum rather than having one each.
 */
const STATISTIC_OPTIONS = [
  { key: "mean", label: "Average" },
  { key: "max", label: "Max" },
];

/** Neither overlay is on until the reader asks for it. */
const DEFAULT_STATISTICS: string[] = [];

/** A phase type's statistics for one series, and where its max occurred. */
interface SeriesStat {
  mean: number;
  max: number;
  maxAt: number | null;
}

type TypeStats = Map<string, Record<string, SeriesStat>>;

// Shared by the chart and by the loading/empty/error placeholders, so the card
// keeps one height and doesn't jump when the series arrives.
const CHART_HEIGHT = 320;

/**
 * Layout of one Y axis, from the axis line outward: the tick numbers, then the
 * rotated unit label right beside them, then clear space before the next axis.
 *
 * `Y_TICK_AREA` is the tick line (6) and margin (2) plus the widest tick
 * string the chart produces — four digits such as "2250" (VO₂ in ml/min) or
 * "0.165" at 10px — with a few pixels to spare so the label never touches
 * them. The label is anchored to that edge rather than to the axis's outer
 * edge, so it sits by its numbers instead of drifting to the far side of the
 * column, and the leftover `Y_AXIS_GAP` is what separates two adjacent
 * right-hand axes.
 */
const Y_TICK_AREA = 38;
const Y_LABEL_AREA = 12;
const Y_AXIS_GAP = 20;
const Y_AXIS_WIDTH = Y_TICK_AREA + Y_LABEL_AREA + Y_AXIS_GAP;

/**
 * One background colour per phase *type*, so the test's structure reads at a
 * glance: warm-up, the effort, the cool-down. Consecutive phases of the same
 * type share a single band — the ramp steps are separated by divider lines
 * instead of by colour. The stretch before the first phase stays white.
 */
/**
 * X-axis tick that keeps the first and last labels inside the plot.
 *
 * Recharts centres every tick on its value, so the edge labels overhang the plot
 * by half their width — and there is no right Y axis whenever the lines share
 * one unit, leaving nothing to overhang into. Anchoring the ends inward fixes it
 * without padding the chart.
 */
const ElapsedAxisTick = ({
  x,
  y,
  payload,
  index,
  visibleTicksCount,
  theme,
}: any) => (
  <text
    x={x}
    y={y}
    dy={12}
    textAnchor={
      index === 0 ? "start" : index === visibleTicksCount - 1 ? "end" : "middle"
    }
    fontFamily="var(--font-Regular)"
    fontSize={10}
    fill={theme.colors_ds.NEUTRAL_TEXT_WEAK}
  >
    {formatElapsed(Number(payload?.value ?? 0))}
  </text>
);

/**
 * Band fill per phase type, from the design: the warm-up barely tinted, the
 * effort in primary, the cool-down neutral. `opacity` is separate because the
 * ramp band is drawn at 70% there (`rgba(230,240,255,0.7)` = P[10] at 0.7).
 */
const phaseTypeFill = (
  type: string | null | undefined,
  theme: Theme
): { fill: string; opacity: number } => {
  switch (type) {
    case "warmup":
      return { fill: theme.colorTokens.N[10], opacity: 1 };
    case "ramp":
      return { fill: theme.colorTokens.P[10], opacity: 0.7 };
    case "recovery":
      return { fill: theme.colorTokens.N[30], opacity: 1 };
    // `resting` (RMR) and anything uncatalogued fall back to neutral.
    default:
      return { fill: theme.colorTokens.N[20], opacity: 1 };
  }
};

/**
 * Phase name above its band — 10px medium, N[70].
 *
 * Rendered verbatim: the design lowercases it, but the service's own casing
 * ("Active Recovery", "Warm-Up") is what the rest of the page shows, and a
 * transform here would make the chart disagree with the phases panel.
 */
const PhaseBandLabel = ({ viewBox, text, theme }: any) => {
  if (!viewBox || !text) return null;
  const { x = 0, width = 0, y = 0 } = viewBox;
  return (
    <text
      x={x + width / 2}
      y={y - 4}
      textAnchor="middle"
      fontFamily="var(--font-Medium)"
      fontSize={10}
      letterSpacing="0.512px"
      fill={theme.colorTokens.N[70]}
    >
      {text}
    </text>
  );
};

/**
 * Rotated unit label beside a Y axis's tick numbers.
 *
 * Recharts hands the axis's own box in `viewBox`; the axis line is at its
 * right edge for a left axis and its left edge for a right axis, with the
 * ticks extending inward from there. The label is placed `Y_TICK_AREA` past
 * that line, reading bottom-up on the left and top-down on the right.
 */
const AxisUnitLabel = ({ viewBox, text, orientation, theme }: any) => {
  if (!viewBox || !text) return null;
  const { x = 0, y = 0, width = 0, height = 0 } = viewBox;
  const isLeft = orientation === "left";
  const cx = isLeft
    ? x + width - Y_TICK_AREA - Y_LABEL_AREA / 2
    : x + Y_TICK_AREA + Y_LABEL_AREA / 2;
  const cy = y + height / 2;
  return (
    <text
      x={cx}
      y={cy}
      transform={`rotate(${isLeft ? -90 : 90} ${cx} ${cy})`}
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="var(--font-Medium)"
      fontSize={11}
      fill={theme.colors_ds.NEUTRAL_TEXT_WEAK}
    >
      {text}
    </text>
  );
};

interface PnoeMeasurementChartProps {
  measurementId: string;
  phases: PnoePhase[];
  catalogs: PnoeMeasurementCatalogs | null;
  /** True while the test is still recording — the series is refetched on poll. */
  live: boolean;
  /** Opens the phases side panel. Omitted hides the "Edit phases" button. */
  onEditPhases?: () => void;
  /** Greys the button out, with this as its tooltip. */
  editPhasesDisabledReason?: string | null;
}

/**
 * One chart for the whole measurement.
 *
 * Replaces the old page's stack of hardcoded graphs: a single plot, any number
 * of measures at a time, phases drawn as background bands.
 *
 * `/vo2-data` caps `limit` at 100, so a long test is a few dozen sequential
 * requests — hence the explicit loading progress.
 */
const PnoeMeasurementChart: React.FC<PnoeMeasurementChartProps> = ({
  measurementId,
  phases,
  catalogs,
  live,
  onEditPhases,
  editPhasesDisabledReason,
}) => {
  const theme: Theme = useTheme();
  const [vo2Records, setVo2Records] = useState<PnoeVo2Record[]>([]);
  const [fields, setFields] = useState<PnoeVo2Field[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadedCount, setLoadedCount] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Empty until the columns arrive; `PREFERRED_SERIES` is applied then.
  const [selectedSeries, setSelectedSeries] = useState<string[]>([]);
  const [selectedStatistics, setSelectedStatistics] =
    useState<string[]>(DEFAULT_STATISTICS);
  // At most one key, because the picker is the shared multi-select
  // `FilterSelect` and `changeSmoothing` keeps it single-choice. Empty means
  // raw: the default, and what the badge-less trigger reads as.
  const [selectedSmoothing, setSelectedSmoothing] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        // The column metadata rides along with every page; take it from the
        // first one that carries it.
        let columns: PnoeVo2Field[] = [];
        const result = await fetchAllBreathPages<PnoeVo2Record>(
          async (params) => {
            const page = await getPnoeVo2Data(measurementId, params);
            if (!columns.length && Array.isArray(page?.fields)) {
              columns = page.fields;
            }
            return page;
          },
          { onProgress: (loaded) => !cancelled && setLoadedCount(loaded) }
        );
        if (cancelled) return;
        setFields(columns);
        setVo2Records(result.data);
        setTruncated(result.truncated);
      } catch (e) {
        if (cancelled) return;
        console.warn("Error loading VO2 data:", e);
        setError("Couldn't load the measurement data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // `live` is in the deps so finishing a recording pulls the full series.
  }, [measurementId, live]);

  const chartSeries = useMemo(
    () => buildSeriesDefinitions(fields, vo2Records),
    [fields, vo2Records]
  );

  const seriesOptions = useMemo(
    () =>
      chartSeries.map((series) => ({
        key: series.key,
        label: series.unit ? `${series.label} (${series.unit})` : series.label,
      })),
    [chartSeries]
  );

  // A different measurement is a different set of columns: start over.
  useEffect(() => {
    setSelectedSeries([]);
  }, [measurementId]);

  // Open on the preferred measures once the columns are known, without
  // disturbing a selection the user has already made.
  useEffect(() => {
    if (!chartSeries.length) return;
    setSelectedSeries((current) => {
      if (current.length) return current;
      const available = chartSeries.map((series) => series.key);
      const preferred = PREFERRED_SERIES.filter((key) =>
        available.includes(key)
      );
      return preferred.length ? preferred : available.slice(0, 2);
    });
  }, [chartSeries]);

  const smoothingWindow =
    SMOOTHING_OPTIONS.find((option) => option.key === selectedSmoothing[0])
      ?.windowSize ?? 1;

  // Build, smooth, then decimate — in that order. Smoothing needs consecutive
  // breaths as neighbours; after decimation a "neighbour" is every nth breath.
  // The per-phase statistics and the tooltip read these same points, so what
  // they report always matches the drawn line.
  const points = useMemo(
    () =>
      decimatePoints(
        smoothChartPoints(
          buildChartPoints(vo2Records, selectedSeries),
          selectedSeries,
          smoothingWindow
        )
      ),
    [vo2Records, selectedSeries, smoothingWindow]
  );

  const activeSeries = useMemo(
    () => chartSeries.filter((s) => selectedSeries.includes(s.key)),
    [chartSeries, selectedSeries]
  );

  // One Y axis per distinct unit: the first picked unit takes the left axis
  // and every further unit gets its own axis on the right, so two measures never share a scale
  // they don't share a unit with. Recharts lays consecutive right axes out
  // side by side. Each axis is labelled with its unit; a unitless measure
  // (RER) is labelled with the measure's own name instead.
  //
  // A measure the device never recorded (every point null) gets no axis: it
  // draws no line, and recharts would still reserve a blank column for its
  // scale. Its `axisFor` falls back to the left axis, where nothing is drawn.
  const yAxes = useMemo(() => {
    const plotted = activeSeries.filter((series) =>
      points.some((point) => point[series.key] != null)
    );
    const units: string[] = [];
    (plotted.length ? plotted : activeSeries).forEach((series) => {
      if (!units.includes(series.unit)) units.push(series.unit);
    });
    return units.map((unit, index) => ({
      // The first axis keeps the id "left": the phase bands and boundary
      // lines anchor to it, and they need an axis that always exists.
      id: index === 0 ? "left" : `right-${index}`,
      unit,
      orientation: (index === 0 ? "left" : "right") as "left" | "right",
      label:
        unit ||
        activeSeries
          .filter((series) => series.unit === unit)
          .map((series) => series.label)
          .join(", "),
    }));
  }, [activeSeries, points]);
  const axisFor = (unit: string) =>
    yAxes.find((axis) => axis.unit === unit)?.id ?? "left";

  // The design's four series colours, assigned by pick order — blue, amber,
  // green, red (node 16883:66653). Exactly four, which is why `MAX_SERIES`
  // caps the picker: a fifth line would have no colour of its own.
  const lineColors = [
    theme.colorTokens.P[80],
    theme.colorTokens.W[60],
    theme.colorTokens.S[50],
    theme.colorTokens.D[70],
  ];

  /**
   * Adding a fifth measure is ignored rather than rejected with a message:
   * the picker shows four ticks, and the next tick simply does not take until
   * one is cleared. Deselecting always works.
   */
  const changeSelection = (next: string[]) => {
    if (next.length <= MAX_SERIES) return setSelectedSeries(next);
    // Keep what was already plotted, in its existing order, so colours do not
    // reshuffle when a rejected pick is dropped.
    setSelectedSeries(
      selectedSeries.filter((key) => next.includes(key)).slice(0, MAX_SERIES)
    );
  };

  // Per-phase spans, clipped to the plotted domain. A phase can begin before the
  // first breath sample (the device records while the subject settles), and
  // Recharts DISCARDS a ReferenceArea outside the axis domain rather than
  // clipping it — so without this the first band silently vanishes.
  //
  // `elapsed_time` is the phase's START offset (verified against the data: it
  // yields uniform ramp steps and an Active Recovery matching the HR decline).
  /**
   * Single choice out of a multi-select picker: the key that was just ticked
   * wins; unticking the current one (or Clear) leaves nothing selected, which
   * is raw.
   */
  const changeSmoothing = (next: string[]) => {
    const added = next.find((key) => !selectedSmoothing.includes(key));
    setSelectedSmoothing(added ? [added] : []);
  };

  const phaseSpans = useMemo(() => {
    if (!phases.length || !points.length) return [];
    const firstElapsed = points[0].elapsed;
    const lastElapsed = points[points.length - 1].elapsed;
    return phases
      .map((phase, index) => {
        const rawStart = toNumber(phase.elapsed_time);
        if (rawStart === null) return null;
        const nextStart = toNumber(phases[index + 1]?.elapsed_time);
        const start = Math.max(rawStart, firstElapsed);
        const end = Math.min(nextStart ?? lastElapsed, lastElapsed);
        if (end <= start) return null;
        return {
          id: phase.id,
          type: phase.type,
          name: phase.name,
          start,
          end,
        };
      })
      .filter(Boolean) as {
      id: string;
      type: string | null;
      name: string | null;
      start: number;
      end: number;
    }[];
  }, [phases, points]);

  // One band per run of consecutive same-type phases, each labelled with its
  // type above the plot — the merged ramp steps read as one "ramp test", which
  // is what the design names them.
  const phaseBands = useMemo(() => {
    const bands: {
      id: string;
      start: number;
      end: number;
      type: string | null;
      name: string | null;
      label: string;
    }[] = [];
    phaseSpans.forEach((span) => {
      const last = bands[bands.length - 1];
      if (last && last.type === span.type && last.end === span.start) {
        last.end = span.end;
        return;
      }
      bands.push({
        ...span,
        // The service names its phases ("Active Recovery", "Ramp Test",
        // "Warm-Up"); the type label ("Recovery") is only the fallback.
        label: span.name || getPhaseTypeLabel(span.type as any, catalogs),
      });
    });
    return bands;
  }, [phaseSpans, catalogs]);

  // A divider wherever the phase changes *within* a band — which is exactly
  // each ramp-step change. Boundaries between bands need no line: the colour
  // change marks them.
  const phaseBoundaries = useMemo(
    () =>
      phaseSpans
        .filter((span, index) => {
          const prev = phaseSpans[index - 1];
          return prev && prev.type === span.type && prev.end === span.start;
        })
        .map((span) => span.start),
    [phaseSpans]
  );

  /**
   * The whole recording as one span, for a test that reports no phases.
   *
   * An RMR test is often a single undivided effort, and the statistics were
   * derived from the phase spans — so with no phases there was no average and
   * no maximum at all, which is the one case where a single average is most
   * obviously meaningful. Treating the test as one implicit phase restores
   * them.
   *
   * Deliberately NOT added to `phaseBands`: a band would tint the plot and
   * print a phase name above it, and there is no phase to name. Only the
   * statistics fall back.
   */
  const wholeTestSpan = useMemo(() => {
    if (!points.length) return null;
    return {
      id: "whole-test",
      type: null as string | null,
      name: null as string | null,
      start: points[0].elapsed,
      end: points[points.length - 1].elapsed,
    };
  }, [points]);

  /** Spans the statistics are computed over — real phases, else the whole test. */
  const statSpans = useMemo(
    () =>
      phaseSpans.length
        ? phaseSpans
        : wholeTestSpan
        ? [wholeTestSpan]
        : [],
    [phaseSpans, wholeTestSpan]
  );

  /** Bands the mean segments are drawn across, same fallback. */
  const statBands = useMemo(
    () =>
      phaseBands.length
        ? phaseBands
        : wholeTestSpan
        ? [{ ...wholeTestSpan, label: "" }]
        : [],
    [phaseBands, wholeTestSpan]
  );

  /**
   * Mean and max per **phase type**, for every plotted series.
   *
   * Per type, not per phase: a ramp test's eleven steps are one effort, so it
   * gets one average and one maximum rather than eleven of each. Grouping by
   * type also means a warm-up and a recovery keep their own numbers, which is
   * the comparison a reader actually makes.
   */
  const typeStats = useMemo<TypeStats>(() => {
    const resolved: TypeStats = new Map();
    const byType = new Map<
      string,
      Record<
        string,
        { sum: number; count: number; max: number; maxAt: number | null }
      >
    >();
    if (!statSpans.length || !points.length) return resolved;

    statSpans.forEach((span) => {
      const key = String(span.type ?? "unknown");
      const inSpan = points.filter(
        (point) => point.elapsed >= span.start && point.elapsed <= span.end
      );
      const bucket = byType.get(key) ?? {};
      activeSeries.forEach((series) => {
        const values = inSpan
          .map((point) => point[series.key])
          .filter((value): value is number => typeof value === "number");
        if (!values.length) return;
        const existing = (bucket as any)[series.key] as
          | { sum: number; count: number; max: number; maxAt: number | null }
          | undefined;
        // Accumulate sum/count across every span of the type, so the mean is
        // over all of its samples rather than an average of averages.
        // Track the sample the max came from, not just its value: it is
        // marked on the chart, so its x is needed too.
        const localMax = inSpan.reduce<{ x: number; y: number } | null>(
          (best, point) => {
            const value = point[series.key];
            if (typeof value !== "number") return best;
            return !best || value > best.y
              ? { x: point.elapsed, y: value }
              : best;
          },
          null
        );
        const better =
          localMax && (!existing || localMax.y > existing.max)
            ? localMax
            : null;
        (bucket as any)[series.key] = {
          sum: (existing?.sum ?? 0) + values.reduce((a, b) => a + b, 0),
          count: (existing?.count ?? 0) + values.length,
          max: better ? better.y : existing?.max ?? -Infinity,
          maxAt: better ? better.x : existing?.maxAt ?? null,
        };
      });
      byType.set(key, bucket as any);
    });

    // Collapse the accumulators into the stats the chart consumes.
    byType.forEach((bucket, key) => {
      const stats: Record<string, SeriesStat> = {};
      Object.entries(bucket as any).forEach(
        ([seriesKey, acc]: [string, any]) => {
          if (!acc.count) return;
          stats[seriesKey] = {
            mean: acc.sum / acc.count,
            max: acc.max,
            maxAt: acc.maxAt,
          };
        }
      );
      resolved.set(key, stats);
    });
    return resolved;
  }, [statSpans, points, activeSeries]);

  /** The phase covering `elapsed`, for the tooltip's per-type statistics. */
  const spanAt = (elapsed: number) =>
    statSpans.find((span) => elapsed >= span.start && elapsed <= span.end) ??
    null;

  const statsAt = (elapsed: number) =>
    typeStats.get(String(spanAt(elapsed)?.type ?? "unknown")) ?? {};

  const showMean = selectedStatistics.includes("mean");
  const showMax = selectedStatistics.includes("max");

  const headerRight = (
    <div style={styles.headerControls}>
      {onEditPhases &&
        (() => {
          const button = (
            <ActionButton
              label="Edit phases"
              isDisable={Boolean(editPhasesDisabledReason)}
              icon={
                <EditSvg
                  color={
                    editPhasesDisabledReason
                      ? theme.colors_ds.NEUTRAL_ICON_WEAK
                      : theme.colors_ds.PRIMARY_ICON_MAIN
                  }
                />
              }
              onClick={onEditPhases}
              sx={styles.compactButton}
            />
          );
          // The tooltip wraps the button rather than sitting on it: a disabled
          // MUI button fires no pointer events, so its own would never show.
          return editPhasesDisabledReason ? (
            <CustomTooltip
              message={editPhasesDisabledReason}
              customHeight="auto"
            >
              <span>{button}</span>
            </CustomTooltip>
          ) : (
            button
          );
        })()}
      <FilterSelect
        title="Smoothing"
        options={SMOOTHING_OPTIONS}
        selectedKeys={selectedSmoothing}
        onSelectionChange={changeSmoothing}
        compact
      />
      <FilterSelect
        title="Statistics"
        options={STATISTIC_OPTIONS}
        selectedKeys={selectedStatistics}
        onSelectionChange={setSelectedStatistics}
        compact
      />
      <FilterSelect
        title="Measures"
        subtitle={`Up to ${MAX_SERIES} at a time`}
        options={seriesOptions}
        selectedKeys={selectedSeries}
        onSelectionChange={changeSelection}
        compact
      />
    </div>
  );

  return (
    <PnoeDetailSection title="Breath data" headerRight={headerRight} plainBody>
      {error ? (
        <div style={styles.placeholder(theme)}>{error}</div>
      ) : loading ? (
        <div style={styles.placeholder(theme)}>
          Loading measurement data…{" "}
          {loadedCount ? `${loadedCount} records` : ""}
        </div>
      ) : points.length === 0 ? (
        <div style={styles.placeholder(theme)}>
          {vo2Records.length === 0
            ? "No measurement data recorded for this test"
            : "Select a measure to plot"}
        </div>
      ) : (
        <>
          <div style={styles.chartWrapper}>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <LineChart
                data={points}
                // 24px of headroom: the phase names are drawn above the plot
                // area, and at top:8 they were clipped by the card.
                margin={{ top: 24, right: 8, bottom: 8, left: 0 }}
              >
                {phaseBands.map((band) => {
                  const { fill, opacity } = phaseTypeFill(band.type, theme);
                  return (
                    <ReferenceArea
                      key={band.id}
                      x1={band.start}
                      x2={band.end}
                      yAxisId="left"
                      fill={fill}
                      fillOpacity={opacity}
                      stroke="none"
                      label={<PhaseBandLabel text={band.label} theme={theme} />}
                    />
                  );
                })}
                {phaseBoundaries.map((elapsed) => (
                  <ReferenceLine
                    key={`boundary-${elapsed}`}
                    x={elapsed}
                    yAxisId="left"
                    stroke={theme.colorTokens.N[60]}
                    strokeDasharray="3 3"
                    strokeWidth={1}
                  />
                ))}
                <CartesianGrid
                  stroke={theme.colors_ds.NEUTRAL_BORDER_WEAK}
                  vertical={false}
                />
                <XAxis
                  dataKey="elapsed"
                  type="number"
                  domain={["dataMin", "dataMax"]}
                  tick={(props) => <ElapsedAxisTick {...props} theme={theme} />}
                  stroke={theme.colors_ds.NEUTRAL_BORDER_WEAK}
                />
                {yAxes.map((axis) => (
                  <YAxis
                    key={axis.id}
                    yAxisId={axis.id}
                    orientation={axis.orientation}
                    tick={styles.axisTick(theme)}
                    stroke={theme.colors_ds.NEUTRAL_BORDER_WEAK}
                    width={Y_AXIS_WIDTH}
                    label={
                      <AxisUnitLabel
                        text={axis.label}
                        orientation={axis.orientation}
                        theme={theme}
                      />
                    }
                  />
                ))}
                <Tooltip
                  content={(props: any) => (
                    <ChartTooltip
                      {...props}
                      activeSeries={activeSeries}
                      phases={phases}
                      catalogs={catalogs}
                      stats={statsAt(Number(props?.label ?? 0))}
                      showMean={showMean}
                      showMax={showMax}
                    />
                  )}
                />
                {/* Per-phase mean: one dashed segment inside each phase,
                    coloured like its line. Drawn before the lines so a line
                    always sits on top of its own average. */}
                {(showMean ? statBands : []).map((band) =>
                  activeSeries.map((series, index) => {
                    const stat = typeStats.get(
                      String(band.type ?? "unknown")
                    )?.[series.key];
                    if (!stat) return null;
                    return (
                      <ReferenceLine
                        key={`mean-${band.id}-${series.key}`}
                        yAxisId={axisFor(series.unit)}
                        segment={[
                          { x: band.start, y: stat.mean },
                          { x: band.end, y: stat.mean },
                        ]}
                        stroke={lineColors[index % lineColors.length]}
                        strokeDasharray="4 4"
                        strokeWidth={1.5}
                        ifOverflow="hidden"
                      />
                    );
                  })
                )}
                {/* The max of each phase type, drawn as a larger dot so it
                    can be found without hunting for it. The tooltip still
                    names it when the cursor lands there. */}
                {showMax &&
                  [...typeStats.entries()].map(([type, stats]) =>
                    activeSeries.map((series, index) => {
                      const stat = stats[series.key];
                      if (!stat || stat.maxAt === null) return null;
                      const color = lineColors[index % lineColors.length];
                      return (
                        <ReferenceDot
                          key={`max-${type}-${series.key}`}
                          yAxisId={axisFor(series.unit)}
                          x={stat.maxAt}
                          y={stat.max}
                          r={5}
                          fill={color}
                          stroke={theme.colors_ds.NEUTRAL_GENERAL_WHITE}
                          strokeWidth={2}
                          ifOverflow="hidden"
                          isFront
                        />
                      );
                    })
                  )}
                {activeSeries.map((series, index) => (
                  <Line
                    key={series.key}
                    yAxisId={axisFor(series.unit)}
                    type="monotone"
                    dataKey={series.key}
                    name={series.label}
                    stroke={lineColors[index % lineColors.length]}
                    strokeWidth={2}
                    dot={false}
                    // A dropped breath leaves a gap rather than a straight line
                    // across missing data.
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.legendRow}>
            {activeSeries.map((series, index) => (
              <span key={series.key} style={styles.legendItem(theme)}>
                <span
                  style={styles.seriesDot(
                    lineColors[index % lineColors.length]
                  )}
                />
                {series.label}
                {series.unit ? ` (${series.unit})` : ""}
              </span>
            ))}
          </div>

          {truncated && (
            <div style={styles.legendNote(theme)}>
              Showing the first {vo2Records.length} records — this test has more
              data than one page load fetches.
            </div>
          )}
        </>
      )}
    </PnoeDetailSection>
  );
};

interface ChartTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: number;
  activeSeries: PnoeSeriesDefinition[];
  phases: PnoePhase[];
  catalogs: PnoeMeasurementCatalogs | null;
  /** The hovered phase type's mean and max, per series. */
  stats: Record<string, { mean: number; max: number }>;
  showMean: boolean;
  showMax: boolean;
}

/**
 * The phase in effect at `elapsed` — the last one that has started.
 *
 * Returns null before the first phase begins, which is the untinted stretch at
 * the left of the chart where the device is recording but the protocol has not
 * started.
 */
const phaseAt = (phases: PnoePhase[], elapsed: number): PnoePhase | null => {
  let current: PnoePhase | null = null;
  for (const phase of phases) {
    const start = toNumber(phase.elapsed_time);
    if (start === null || start > elapsed) continue;
    current = phase;
  }
  return current;
};

const ChartTooltip: React.FC<ChartTooltipProps> = ({
  active,
  payload,
  label,
  activeSeries,
  phases,
  catalogs,
  stats,
  showMean,
  showMax,
}) => {
  const theme: Theme = useTheme();
  if (!active || !payload?.length) return null;
  const phase = phaseAt(phases, Number(label ?? 0));
  const load = phase && hasPhaseLoad(phase) ? formatPhaseLoad(phase) : null;
  return (
    <div style={styles.tooltip(theme)}>
      <div style={styles.tooltipTime(theme)}>
        {formatElapsed(Number(label ?? 0))}
      </div>
      {phase && (
        <div style={styles.tooltipPhase(theme)}>
          {[phase.name || getPhaseTypeLabel(phase.type, catalogs), load]
            .filter(Boolean)
            .join(" · ")}
        </div>
      )}
      <div style={styles.tooltipRows(theme)}>
        {payload.map((entry) => {
          const series = activeSeries.find((s) => s.key === entry.dataKey);
          const stat = stats[entry.dataKey];
          // Named here as well as marked on the line, so landing on the dot
          // confirms what it is.
          const isMax =
            showMax &&
            typeof entry.value === "number" &&
            typeof stat?.max === "number" &&
            Math.abs(entry.value - stat.max) < 1e-6;
          return (
            <div key={entry.dataKey} style={styles.tooltipRow}>
              <span style={styles.tooltipLabel(theme)}>
                <span style={styles.seriesDot(entry.stroke, isMax)} />
                <span>
                  {series?.label ?? entry.dataKey}
                  {" → "}
                  <span style={styles.tooltipValue(theme, isMax)}>
                    {formatSeriesValue(entry.value, series)}
                  </span>
                  {series?.unit ? (
                    <span style={styles.tooltipUnit(theme)}>
                      {" "}
                      {series.unit}
                    </span>
                  ) : null}
                  {isMax && <span style={styles.tooltipMax(theme)}> max</span>}
                </span>
              </span>
              {showMean && typeof stat?.mean === "number" && (
                <span style={styles.tooltipAverage(theme)}>
                  avg {formatSeriesValue(stat.mean, series)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PnoeMeasurementChart;

const styles = {
  chartWrapper: {
    width: "100%",
  },
  headerControls: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap" as "wrap",
    justifyContent: "flex-end",
  },
  // The design's controls are 12px text in a 34px box, where ActionButton
  // defaults to 14px in 38px.
  compactButton: {
    height: "34px",
    fontSize: "12px",
    padding: "8px 12px",
    gap: "8px",
    flexShrink: 0,
    "& .MuiBox-root": { flex: "0 0 auto" },
  },
  placeholder: (theme: Theme) => ({
    height: `${CHART_HEIGHT}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    textAlign: "center" as "center",
  }),
  axisTick: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: 10,
    fill: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  legendRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap" as "wrap",
    paddingTop: "8px",
  },
  legendItem: (theme: Theme) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  // Colored dot + label, the house pattern for a keyed value (status badges use
  // the same 8px circle with an 8px gap).
  // `isMax` grows the dot and rings it, so the tooltip row for a phase
  // maximum reads differently from an ordinary sample.
  seriesDot: (color: string, isMax = false) => ({
    display: "inline-block",
    width: isMax ? "10px" : "8px",
    height: isMax ? "10px" : "8px",
    borderRadius: "50%",
    backgroundColor: color,
    boxShadow: isMax ? `0 0 0 2px ${color}40` : "none",
    flexShrink: 0,
  }),
  tooltipPhase: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  legendNote: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  tooltip: (theme: Theme) => ({
    width: "240px",
    boxSizing: "border-box" as "border-box",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    borderRadius: "5px",
    padding: "8px",
    boxShadow: "var(--box-shadow-general)",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  }),
  // Time is the tooltip's title; the phase line below it is helper text.
  tooltipTime: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "10px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  // Rule separating the header from the readings.
  tooltipRows: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
    marginTop: "8px",
  }),
  // Label left, value right — so a column of readings stays scannable.
  tooltipRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },
  tooltipLabel: (theme: Theme) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  tooltipValue: (theme: Theme, isMax = false) => ({
    fontFamily: isMax ? "var(--font-Bold)" : "var(--font-Medium)",
    // The strongest neutral rather than the primary blue: blue reads as a
    // link or a series colour here, and the row already carries the series'
    // own colour in its dot.
    color: isMax
      ? theme.colors_ds.NEUTRAL_TEXT_STRONG
      : theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  tooltipUnit: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  /** Names the emphasis, so a bolder value is not left to be guessed at. */
  tooltipMax: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    textTransform: "uppercase" as "uppercase",
    letterSpacing: "0.4px",
  }),
  /** The phase-type average, right-aligned as the design places it. */
  tooltipAverage: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    whiteSpace: "nowrap" as "nowrap",
    flexShrink: 0,
  }),
};
